import type { NavigationScreen, ScreenParameters, SpecificNavigationProp } from 'app/types/navigation';
import { withErrorBoundary } from 'app/components/error-boundary';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Page } from 'app/design/primitives/page';
import { Column, Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';
import { Button } from 'app/design/primitives/button';
import { useNavigationOptions } from 'app/hooks/navigation';

import { Accessory } from 'app/design/primitives/page/accessory';
import { Platform, Pressable, View } from 'react-native';
import { StorageKey, useStoredValue } from 'app/lib/storage';
import { WhiteLabel } from 'app/types/white-labels';
import { useUISize, usePageTitle } from 'app/utils/web-helpers';
import { isWhiteLabel } from 'app/utils/helpers';
import {
  IlluHauteGaspesie,
  IlluMutuo,
  IlluRefrain,
  IlluSerres,
  type IlluSvgSize,
} from 'app/screens/management/offer-availability-popup-illustrations';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { LogoHauteGaspesieInvert } from 'app/design/branding/logo';
const theme = createDSTheme();

export function useOfferAvailabilityPopup<T extends keyof ScreenParameters>(
  storageKey: StorageKey,
  navigation: SpecificNavigationProp<T>,
  { listingId, isSaleOrDonation = false }: { listingId?: string; isSaleOrDonation?: boolean } = {},
) {
  const [hasSeenPopup, setHasSeenPopup] = useStoredValue<boolean>(storageKey, false);
  return () => {
    if (!isSaleOrDonation && isWhiteLabel() && hasSeenPopup === false) {
      const who =
        storageKey === StorageKey.RefrainBlockedDatesPopupWasShownLenderBefore
          ? 'lender.before'
          : storageKey === StorageKey.RefrainBlockedDatesPopupWasShownLenderAfter
            ? 'lender.after'
            : 'borrower';
      setTimeout(() => {
        navigation.navigate('refrainOfferAvailabilityPopup', { who, listingId }, { pop: true });
        setHasSeenPopup(true);
      }, 100);
      return true;
    }
    return false;
  };
}

interface PopupContentProps {
  who: string;
  textColor: string;
  svgSize?: IlluSvgSize;
  container: React.RefObject<View | null>;
  onLayoutContainer: () => void;
}

function OfferAvailabilityPopupContent({ who, textColor, svgSize, container, onLayoutContainer }: PopupContentProps) {
  const { t } = useTranslation('offerAvailability');

  return (
    <Row style={{ height: '100%', alignItems: 'center' }}>
      <Column
        gap="spacing.general.x4"
        paddingBottom="spacing.general.x8"
        paddingHorizontal="spacing.general.x8"
        maxWidth="100%"
      >
        <Column gap="spacing.general.x2">
          <Text fontSize={24} fontWeight="700" style={{ color: textColor }} variant="bodyBase">
            {t('refrainPopup.hi')}
          </Text>

          <View onLayout={onLayoutContainer} ref={container} style={{ width: '100%', marginLeft: svgSize?.marginLeft }}>
            {isWhiteLabel(WhiteLabel.Refrain) && <IlluRefrain svgSize={svgSize} />}
            {isWhiteLabel(WhiteLabel.SerresTessier) && <IlluSerres svgSize={svgSize} />}
            {isWhiteLabel(WhiteLabel.Mutuo) && <IlluMutuo svgSize={svgSize} />}
            {isWhiteLabel(WhiteLabel.HauteGaspesie) && <IlluHauteGaspesie svgSize={svgSize} />}
          </View>

          <Text fontSize={24} fontWeight="700" style={{ color: textColor }} variant="bodyBase">
            {t(`refrainPopup.text.${who}`)}
          </Text>
        </Column>

        <Text fontSize={16} marginTop="spacing.general.x2" style={{ color: textColor }} variant="bodyBase">
          {t(`refrainPopup.subtext.${who}`)}
        </Text>
      </Column>
    </Row>
  );
}

const RefrainOfferAvailabilityPopupScreenComponent: NavigationScreen<'refrainOfferAvailabilityPopup'> = ({
  navigation,
  route,
}) => {
  const setPageTitle = usePageTitle('offerAvailability');
  useEffect(() => setPageTitle('title'), [setPageTitle]);
  const { isSmallUI, isLargeUI } = useUISize();
  const { t } = useTranslation('offerAvailability');

  const [, setHasSeenPopupLenderBefore] = useStoredValue<boolean>(
    StorageKey.RefrainBlockedDatesPopupWasShownLenderBefore,
    false,
  );

  const backgroundColor = isWhiteLabel(WhiteLabel.Refrain) ? '#6A3045' : theme.colors['surface.tertiary'];
  const textColor = isWhiteLabel(WhiteLabel.Refrain) ? '#EDF5FF' : theme.colors['text.primary'];
  const isMutuo = isWhiteLabel(WhiteLabel.Mutuo);
  const isHauteGaspesie = isWhiteLabel(WhiteLabel.HauteGaspesie);

  useNavigationOptions(navigation, {
    headerStyle: { backgroundColor },
    ...(isMutuo && {
      headerTitle: () => null,
      headerStyle: { backgroundColor: backgroundColor, borderBottomColor: backgroundColor },
    }),
    ...(isHauteGaspesie && {
      headerTitle: () => <LogoHauteGaspesieInvert color={theme.colors.black} />,
    }),
    headerTintColor: textColor,
    headerLeft: () =>
      isLargeUI || Platform.OS === 'android' ? null : (
        <Button onPress={() => navigation.goBack()}>
          <IconX strokeWidth={ICON_STROKE_WIDTH} color={isMutuo ? 'black' : theme.colors['text.primary']} />
        </Button>
      ),

    headerRight: () =>
      isSmallUI || Platform.OS === 'android' ? null : (
        <Button onPress={() => navigation.goBack()}>
          <IconX strokeWidth={ICON_STROKE_WIDTH} color={isMutuo ? 'black' : theme.colors['text.primary']} />
        </Button>
      ),
  });

  const onContinue = () => {
    navigation.goBack();
    if (route.params.who === 'lender.after') {
      setHasSeenPopupLenderBefore(true); // Don't show the popup in the calender too!
      setTimeout(() => {
        navigation.navigate('offer-availability', { listingId: route.params.listingId!, goBack: true }, { pop: true });
      }, 300);
    }
  };

  const [svgSize, setSvgSize] = useState<IlluSvgSize>();
  const container = useRef<View>(null);
  const onLayoutContainer = () => {
    if (!container.current) return;
    if (svgSize) return;
    container.current.measure((_x, _y, width, _height, _pageX, _pageY) => {
      if (isMutuo || isHauteGaspesie) {
        setSvgSize({ height: (width * 223) / 552, width, marginLeft: 0 });
      } else {
        width = (width * 317) / 281;
        setSvgSize({ height: (width * 216) / 317, width, marginLeft: ((-1 * (317 - 281)) / 2) * (width / 317) });
      }
    });
  };

  const contentProps: PopupContentProps = { who: route.params.who, textColor, svgSize, container, onLayoutContainer };

  return (
    <Page
      accessory={
        <Accessory style={{ backgroundColor }}>
          <Column gap="spacing.general.x8">
            <Button
              onPress={onContinue}
              variant={isMutuo ? undefined : 'primary'}
              style={{
                height: 50,
                borderRadius: 25,
                marginHorizontal: theme.spacing['spacing.general.x8'],
                ...(isMutuo && { backgroundColor: 'white', alignSelf: 'flex-end' }),
              }}
            >
              <Text
                fontWeight="700"
                style={{ color: isMutuo ? 'black' : theme.colors['surface.default'] }}
                variant="bodyBase"
              >
                {t(route.params.who === 'lender.after' ? 'refrainPopup.reserveDates' : 'refrainPopup.continue')}
              </Text>
            </Button>
            {route.params.who === 'lender.after' && (
              <Pressable onPress={() => navigation.goBack()}>
                <Text
                  fontWeight="700"
                  style={{
                    color: '#EDF5FF',
                    textDecorationLine: 'underline',
                    marginBottom: theme.spacing['spacing.general.x4'],
                  }}
                  textAlign="center"
                >
                  {t('refrainPopup.later')}
                </Text>
              </Pressable>
            )}
          </Column>
        </Accessory>
      }
      isModal
      stretchToFill
      style={{ backgroundColor }}
    >
      <OfferAvailabilityPopupContent {...contentProps} />
    </Page>
  );
};

export const RefrainOfferAvailabilityPopupScreen: NavigationScreen<'refrainOfferAvailabilityPopup'> = withErrorBoundary(
  RefrainOfferAvailabilityPopupScreenComponent,
);
